import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import useShopStatus from './hooks/useShopStatus';
import StoreClosed from './components/StoreClosed';
import { useCustomerAuth } from './context/CustomerAuthContext';
import CustomerAuthModal from './components/CustomerAuthModal';
import CustomerProfile from './components/CustomerProfile';
import logger from './utils/logger';
import toast from './utils/toast';
import './CustomerApp.css';

// Layout Components (loaded immediately)
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Lazy-loaded Pages for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const MenuPage = lazy(() => import('./pages/MenuPage'));
const LocationPage = lazy(() => import('./pages/LocationPage'));
const AllLocationsPage = lazy(() => import('./pages/AllLocationsPage'));
const OutletSelectionPage = lazy(() => import('./pages/OutletSelectionPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const OrderSuccessPage = lazy(() => import('./pages/OrderSuccessPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const OrderTracking = lazy(() => import('./components/OrderTracking'));

// Loading fallback component
const PageLoader = () => (
  <div className="oz-page-loader">
    <div className="oz-loading-content">
      <span className="oz-loading-icon">🍛</span>
      <p>Loading...</p>
      <div className="oz-loading-spinner"></div>
    </div>
  </div>
);

const CustomerApp = () => {
  const { shopOnline, loading: shopLoading } = useShopStatus();
  const { currentUser } = useCustomerAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // State
  // Initialize cart from localStorage to persist across page refreshes
  const [cartItems, setCartItems] = useState(() => {
    try {
      const savedCart = localStorage.getItem('odisha-zaika-cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      logger.error('Error loading cart from localStorage:', error);
      return [];
    }
  });
  const [showCart, setShowCart] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('odisha-zaika-cart', JSON.stringify(cartItems));
    } catch (error) {
      logger.error('Error saving cart to localStorage:', error);
    }
  }, [cartItems]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Cart Functions
  const addToCart = (item, isVeg) => {
    const existingIndex = cartItems.findIndex(
      ci => ci.name === item.name && ci.isVeg === isVeg
    );

    if (existingIndex > -1) {
      const newCartItems = [...cartItems];
      newCartItems[existingIndex].quantity += 1;
      setCartItems(newCartItems);
      toast.success(`${item.name} quantity updated!`);
    } else {
      setCartItems([...cartItems, {
        name: item.name,
        price: item.price || 0,
        description: item.description || '',
        isVeg: isVeg,
        quantity: 1,
        image: item.image || null
      }]);
      toast.success(`${item.name} added to cart!`);
    }
  };

  const updateQuantity = (index, delta) => {
    const newCartItems = [...cartItems];
    newCartItems[index].quantity += delta;
    if (newCartItems[index].quantity <= 0) {
      newCartItems.splice(index, 1);
    }
    setCartItems(newCartItems);
  };

  const removeFromCart = (index) => {
    const itemName = cartItems[index].name;
    const newCartItems = [...cartItems];
    newCartItems.splice(index, 1);
    setCartItems(newCartItems);
    toast.info(`${itemName} removed from cart`);
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('odisha-zaika-cart');
  };

  const cartItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  // Handle Auth Click
  const handleAuthClick = () => {
    if (currentUser) {
      setShowProfileModal(true);
    } else {
      setShowAuthModal(true);
    }
  };

  // Check if shop is closed
  if (shopLoading) {
    return (
      <div className="oz-loading-screen">
        <div className="oz-loading-content">
          <span className="oz-loading-icon">🍛</span>
          <p>Loading Odisha Zaika...</p>
          <div className="oz-loading-spinner"></div>
        </div>
      </div>
    );
  }

  // Temporarily disabled for testing - uncomment to enable shop status check
  // if (!shopOnline) {
  //   return <StoreClosed />;
  // }

  return (
    <div className="oz-customer-app">
      {/* Header */}
      <Header
        cartItemCount={cartItemCount}
        onCartClick={() => setShowCart(true)}
        onAuthClick={handleAuthClick}
      />

      {/* Main Content */}
      <main className="oz-main-content">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route
              path="/"
              element={
                <HomePage
                  onAddToCart={addToCart}
                  cartItems={cartItems}
                  onUpdateQuantity={updateQuantity}
                />
              }
            />
            <Route
              path="/menu"
              element={
                <MenuPage
                  onAddToCart={addToCart}
                  cartItems={cartItems}
                  onUpdateQuantity={updateQuantity}
                />
              }
            />
            <Route
              path="/location/:slug"
              element={
                <LocationPage
                  onAddToCart={addToCart}
                  cartItems={cartItems}
                  onUpdateQuantity={updateQuantity}
                />
              }
            />
            <Route path="/locations" element={<AllLocationsPage />} />
            <Route path="/select-outlet" element={<OutletSelectionPage />} />
            <Route
              path="/checkout"
              element={
                <CheckoutPage
                  cartItems={cartItems}
                  onClearCart={clearCart}
                />
              }
            />
            <Route path="/order-success" element={<OrderSuccessPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/track-order" element={<OrderTracking />} />
            {/* Fallback to Menu for unmatched routes */}
            <Route path="*" element={<HomePage onAddToCart={addToCart} cartItems={cartItems} onUpdateQuantity={updateQuantity} />} />
          </Routes>
        </Suspense>
      </main>

      {/* Footer */}
      <Footer />

      {/* Cart Drawer */}
      {showCart && (
        <div className="oz-cart-drawer-overlay" onClick={() => setShowCart(false)}>
          <div className="oz-cart-drawer" onClick={e => e.stopPropagation()}>
            <div className="oz-cart-header">
              <h2>Your Cart</h2>
              <button className="oz-cart-close" onClick={() => setShowCart(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>

            {cartItems.length === 0 ? (
              <div className="oz-cart-empty">
                <span className="oz-cart-empty-icon">🛒</span>
                <h3>Your cart is empty</h3>
                <p>Add items from the menu to get started!</p>
                <button className="oz-btn oz-btn-primary" onClick={() => setShowCart(false)}>
                  Browse Menu
                </button>
              </div>
            ) : (
              <>
                <div className="oz-cart-items">
                  {cartItems.map((item, index) => (
                    <div key={index} className="oz-cart-item">
                      <div className="oz-cart-item-left">
                        <span className={`oz-veg-badge ${item.isVeg ? 'veg' : 'nonveg'}`}>
                          <span className="oz-veg-dot"></span>
                        </span>
                        <div className="oz-cart-item-info">
                          <span className="oz-cart-item-name">{item.name}</span>
                          <button className="oz-cart-remove" onClick={() => removeFromCart(index)}>
                            Remove
                          </button>
                        </div>
                      </div>
                      <div className="oz-cart-item-right">
                        <div className="oz-cart-qty">
                          <button onClick={() => updateQuantity(index, -1)}>−</button>
                          <span>{item.quantity}</span>
                          <button onClick={() => updateQuantity(index, 1)}>+</button>
                        </div>
                        <span className="oz-cart-item-price">₹{item.price * item.quantity}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="oz-cart-footer">
                  <div className="oz-cart-subtotal">
                    <span>Subtotal</span>
                    <span>₹{cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0)}</span>
                  </div>
                  <p className="oz-cart-note">Delivery charges calculated at checkout</p>
                  <button
                    className="oz-btn oz-btn-primary oz-btn-full"
                    onClick={() => {
                      setShowCart(false);
                      navigate('/checkout');
                    }}
                  >
                    Proceed to Checkout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Auth Modal */}
      {showAuthModal && (
        <CustomerAuthModal onClose={() => setShowAuthModal(false)} />
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <CustomerProfile onClose={() => setShowProfileModal(false)} />
      )}
    </div>
  );
};

export default CustomerApp;
